import type { ILocale, ISiteLocalization, ISiteMetadata, ISiteNavigation } from '@anywayseo/tools';
import { getLocale, getLocales, getNavigation, getSiteName } from '@anywayseo/tools';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { localNavigation } from 'data';

const GET_SITE_QUERY = graphql`
  query GetSite {
    site {
      siteMetadata {
        title
        description
        siteUrl
        currency
        locale
        locales {
          code
          name
        }
      }
    }
    file(sourceInstanceName: { eq: "images" }, relativePath: { eq: "logo.png" }) {
      childImageSharp {
        gatsbyImageData(height: 60)
      }
    }
    strapiSite {
      name
      locale
      currency
      seo {
        metaTitle
        metaDescription
      }
      logo {
        localFile {
          childImageSharp {
            gatsbyImageData(height: 60)
          }
        }
      }
      socialNetworks {
        items {
          kind
          url
        }
      }
    }
    strapiNavigation {
      items {
        slug
        locale
        items {
          type
          title
          slug
          path
          items {
            type
            title
            slug
            path
          }
        }
      }
    }
    strapiLocalization {
      locales {
        code
        name
      }
    }
  }
`;

interface IUseSiteReturn {
  metadata: ISiteMetadata;
  navigation: ISiteNavigation;
  localization: ISiteLocalization;
}

function useSite(): IUseSiteReturn {
  const { site, file, strapiSite, strapiNavigation, strapiLocalization } =
    useStaticQuery<Queries.GetSiteQuery>(GET_SITE_QUERY);

  let name;
  let title;
  let description;
  let logoImage;
  let defaultLocaleCode;
  let locales;
  let currency;
  let socialNetworks;

  if (process.env.GATSBY_SITE_SOURCE === 'local') {
    name = getSiteName(site?.siteMetadata?.siteUrl);
    title = site?.siteMetadata?.title;
    description = site?.siteMetadata?.description;
    defaultLocaleCode = site?.siteMetadata?.locale;
    locales = site?.siteMetadata?.locales;
    currency = site?.siteMetadata?.currency;
    logoImage = file?.childImageSharp?.gatsbyImageData;
  } else {
    name = strapiSite?.name;
    title = strapiSite?.seo?.metaTitle;
    description = strapiSite?.seo?.metaDescription;
    defaultLocaleCode = strapiSite?.locale;
    locales = strapiLocalization?.locales;
    currency = strapiSite?.currency;
    logoImage = strapiSite?.logo?.localFile?.childImageSharp?.gatsbyImageData;
    socialNetworks = strapiSite?.socialNetworks?.items;
  }

  const defaultLocale = getLocale(defaultLocaleCode as ILocale['code'], locales as ILocale[]);
  const logo = logoImage ? (
    <GatsbyImage image={logoImage} alt='Logo' objectFit='contain' style={{ display: 'flex' }} />
  ) : (
    ''
  );

  return {
    metadata: {
      name: name ?? '',
      lang: defaultLocale.code,
      logo,
      seo: {
        title: title ?? '',
        description: description ?? '',
      },
      socialNetworks: socialNetworks as ISiteMetadata['socialNetworks'],
    },
    localization: {
      currency: (currency ?? 'USD') as ISiteLocalization['currency'],
      defaultLocale,
      locales: getLocales(defaultLocale.code, locales as ILocale[]),
    },
    navigation: getNavigation(localNavigation, strapiNavigation),
  };
}

export default useSite;
